import {DataTable} from "../DataTable/DataTable";
import {compareNumber, containerLevelHistoryTypes, DVIREntity} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {Box, Group, Modal, ModalProps, Text} from "@mantine/core";
import {usePagination} from "../DataTable/usePagination";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {useDVIRDataTableFilter} from "../DVIRDataTable/useDVIRDataTableFilter";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";
import {MouseEvent, useCallback, useMemo} from "react";
import {DataTableColumn} from "mantine-datatable";
import {sortBy} from "lodash";
import {IconAlertTriangleFilled, IconCircleCheckFilled} from "@tabler/icons-react";
import {FormattedDateTime} from "../../date/FormattedDateTime/FormattedDateTime";
import {EntityAnchor} from "../../anchor/EntityAnchor/EntityAnchor";
import {useNavigate} from "react-router-dom";
import {useDVIRAction} from "../../menu/DVIRActionMenu/useDVIRAction";
import {EntityActionGroup} from "../../group/EntityActionGroup/EntityActionGroup";

const EMPTY_ARRAY: DVIREntity[] = []

export type DVIRDataTableProps = EntityDataTableProps<DVIREntity>

export const DVIRDataTable = (props: DVIRDataTableProps) => {
  const {
    records: dvirs = EMPTY_ARRAY,
    recordsPerPageOptions,
    ...rest
  } = props

  const {t} = useTranslation(["common"]);
  const navigate = useNavigate()

  const {filteredRecords, filterConfig} = useDVIRDataTableFilter({
    records: dvirs
  })

  const sortConfig = useMemo(() => {
    return {
      "personName": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.personName?.toLowerCase()
          }])
        }
      },
      "vehicleName": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.vehicleName?.toLowerCase()
          }])
        }
      },
      "trailerName": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.trailerName?.toLowerCase()
          }])
        }
      },
      "status": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.status?.toLowerCase()
          }])
        }
      }
    }
  }, [])

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'dttm', direction: 'desc'},
    sortConfig: sortConfig,
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const {
    onDVIRActionClick
  } = useDVIRAction()

  const onViewActionClick = useCallback((e: MouseEvent<HTMLButtonElement>, record: DVIREntity) => {
    e.stopPropagation()
    navigate(`/dvirs/profile?id=${record.id}`)
  }, [navigate])

  const onEditActionClick = useCallback((e: MouseEvent<HTMLButtonElement>, record: DVIREntity) => {
    e.stopPropagation()
    onDVIRActionClick(record, 'EDIT')
  }, [onDVIRActionClick])

  const onDeleteActionClick = useCallback((e: MouseEvent<HTMLButtonElement>, record: DVIREntity) => {
    e.stopPropagation()
    onDVIRActionClick(record, 'DELETE')
  }, [onDVIRActionClick])

  const columns = useMemo(() => {
    const values: DataTableColumn<DVIREntity>[] = [
      {
        title: t("common.entity.dvir.table.column.date", "Date"),
        accessor: 'dttm',
        width: 100,
        sortable: true,
        render: ({dttm}) => (
          <FormattedDateTime date={dttm}/>
        ),
      },
      {
        ...getColumnFilterProps(filterConfig, "personName"),
        title: t("common.entity.dvir.table.column.person", "Driver"),
        accessor: 'personName',
        width: 100,
        sortable: true,
        render: ({personId, personName}) => (
          <EntityAnchor
            value={{
              objectId: personId,
              objectName: personName,
              objectType: "PERSON"
            }}/>
        ),
      },
      {
        ...getColumnFilterProps(filterConfig, "vehicleName"),
        title: t("common.entity.dvir.table.column.vehicle.alt", "Truck"),
        accessor: 'vehicleName',
        width: 100,
        sortable: true,
        render: ({vehicleId, vehicleName}) => (
          vehicleId ? <EntityAnchor
            value={{
              objectId: vehicleId,
              objectName: vehicleName,
              objectType: "VEHICLE"
            }}/> : undefined
        ),
      },
      {
        ...getColumnFilterProps(filterConfig, "trailerName"),
        title: t("common.entity.dvir.table.column.trailer", "Trailer"),
        accessor: 'trailerName',
        width: 100,
        sortable: true,
        render: ({trailerId, trailerName}) => (
          trailerId ? <EntityAnchor
            value={{
              objectId: trailerId,
              objectName: trailerName,
              objectType: "TRAILER"
            }}/> : undefined
        ),
      },
      {
        title: t("common.entity.dvir.table.column.defects", "Defects"),
        accessor: 'defectiveItemCount',
        width: 50,
        sortable: true,
      },
      {
        title: t("common.entity.dvir.table.column.review", "Review"),
        accessor: 'satisfactory',
        width: 75,
        sortable: true,
        render: ({satisfactory}) => (
          <Text ta="left" c={satisfactory ? "green" : "red"} fz="xs">
            <Group spacing="xs">
              {satisfactory ? <IconCircleCheckFilled size="14"/> : <IconAlertTriangleFilled size="14"/>}
              {satisfactory ? "Satisfactory" : "Unsatisfactory"}
            </Group>
          </Text>
        )
      },
      {
        ...getColumnFilterProps(filterConfig, "status"),
        title: t("common.entity.dvir.table.column.status", "Status"),
        accessor: 'status',
        width: 75,
        sortable: true,
        render: ({status}) => (
          <Text ta="left" c={status === "Pending Mechanic Review" ? "warning" : ""} fz="xs">
            <Group spacing="xs">
              {status}
            </Group>
          </Text>
        )
      },
      {
        title: '',
        accessor: "actions",
        width: 100,
        textAlignment: 'right',
        render: (dvir) => (
          <EntityActionGroup
            permissionPrefix="dvir"
            onViewActionClick={(e) => onViewActionClick(e, dvir)}
            onEditActionClick={(e) => onEditActionClick(e, dvir)}
            onDeleteActionClick={(e) => onDeleteActionClick(e, dvir)}/>
        )
      }
    ]

    return values
  }, [filterConfig, onDeleteActionClick, onEditActionClick, onViewActionClick, t])

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={columns}
    />
  )
}

export type DVIRDataTableModalProps = Omit<ModalProps, "onSubmit"> & {
  dataTable: Omit<DVIRDataTableProps, 'recordsPerPageOptions'>
}

export const DVIRDataTableModal = (props: DVIRDataTableModalProps) => {
  const {t} = useTranslation(["common"]);

  const {
    title = t<string>("common.entity.dvir.plural", "DVIRs"),
    dataTable,
    ...rest
  } = props;

  return (
    <Modal {...rest} title={title}>
      <Box>
        <DVIRDataTable
          {...dataTable}
          recordsPerPageOptions={[5]}/>
      </Box>
    </Modal>
  );
};
